import { useEffect, useState } from "react";
import { fetchProducts, deleteProduct } from "../api/productApi";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const loadProducts = async () => {
    try {
      setLoading(true);

      const data = await fetchProducts();

      setProducts(data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;

    try {
      await deleteProduct(id);

      toast.success("Product deleted");

      loadProducts();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-sm text-gray-500">
          Loading products...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* ================= HEADER ================= */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-5 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            Products
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Manage your products
          </p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Link
            to="/products/bulk-upload"
            className="inline-flex items-center justify-center bg-gray-800 hover:bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition"
          >
            Bulk Upload
          </Link>

          <button
            onClick={() => navigate("/products/new")}
            className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition"
          >
            + Add Product
          </button>
        </div>
      </div>

      {/* ================= DESKTOP TABLE ================= */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-100">
                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Name
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Price
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Final Price
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Stock
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Status
                </th>

                <th className="px-5 py-4 text-right font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {products.map((p) => (
                <tr
                  key={p._id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition"
                >
                  {/* Name */}
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-800">
                      {p.name}
                    </p>
                  </td>

                  {/* Price */}
                  <td className="px-5 py-4 text-gray-700">
                    ₹
                    {Number(p.price || 0).toLocaleString(
                      "en-IN"
                    )}
                  </td>

                  {/* Final Price */}
                  <td className="px-5 py-4 font-medium text-green-600">
                    ₹
                    {Number(p.finalPrice || 0).toLocaleString(
                      "en-IN"
                    )}
                  </td>

                  {/* Stock */}
                  <td className="px-5 py-4 text-gray-700">
                    {p.quantity}
                  </td>

                  {/* Status */}
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {p.isFeatured && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          Featured
                        </span>
                      )}

                      {p.isOnSale && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                          Sale {p.discountPercent}%
                        </span>
                      )}

                      {!p.isFeatured && !p.isOnSale && (
                        <span className="text-xs text-gray-400">
                          —
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() =>
                          navigate(`/products/edit/${p._id}`)
                        }
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() =>
                          handleDelete(p._id)
                        }
                        className="text-red-500 hover:text-red-700 font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {products.length === 0 && (
          <p className="p-8 text-center text-gray-500">
            No products found
          </p>
        )}
      </div>

      {/* ================= MOBILE PRODUCT CARDS ================= */}
      <div className="md:hidden space-y-3">
        {products.length > 0 ? (
          products.map((p) => (
            <div
              key={p._id}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
            >
              {/* Product Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-800 text-base break-words">
                    {p.name}
                  </h3>

                  <p className="text-xs text-gray-400 mt-1">
                    Product ID: #{p._id.slice(-6)}
                  </p>
                </div>

                {/* Status */}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {p.isFeatured && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
                      Featured
                    </span>
                  )}

                  {p.isOnSale && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium bg-red-100 text-red-600">
                      Sale {p.discountPercent}%
                    </span>
                  )}
                </div>
              </div>

              {/* Product Information */}
              <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500">
                    Price
                  </p>

                  <p className="text-sm font-medium text-gray-800 mt-1">
                    ₹
                    {Number(p.price || 0).toLocaleString(
                      "en-IN"
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">
                    Final
                  </p>

                  <p className="text-sm font-semibold text-green-600 mt-1">
                    ₹
                    {Number(p.finalPrice || 0).toLocaleString(
                      "en-IN"
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">
                    Stock
                  </p>

                  <p className="text-sm font-medium text-gray-800 mt-1">
                    {p.quantity}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={() =>
                    navigate(`/products/edit/${p._id}`)
                  }
                  className="flex-1 py-2 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 text-sm font-medium"
                >
                  Edit
                </button>

                <button
                  onClick={() =>
                    handleDelete(p._id)
                  }
                  className="flex-1 py-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-500">
            No products found
          </div>
        )}
      </div>
    </div>
  );
}